import { useEffect, useMemo, useRef, useState } from "react";

import { SlidersHorizontal } from "lucide-react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import Logo from "src/components/icon/Logo";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "src/components/ui/chart";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { storeIntegrationTypes } from "src/lib/constants";
import { dateFormat } from "src/lib/format";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { useNumberFormat } from "src/lib/hooks/useNumberFormat";
import { cn } from "src/lib/utils";

const CustomBarShape = (props) => {
  const { x, y, width, height, payload, fill } = props;
  const meta = payload?.meta || {};

  const barColor = meta.color || fill;

  return (
    <g>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={meta.hatch ? "transparent" : barColor}
        opacity={0.85}
      />
      {meta.hatch && (
        <>
          <defs>
            <pattern
              id="barHatch"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="6"
                stroke={barColor}
                strokeWidth="2"
              />
            </pattern>
          </defs>
          <Rectangle
            x={x}
            y={y}
            width={width}
            height={height}
            fill="url(#barHatch)"
          />
        </>
      )}
      {meta.label && (
        <text
          x={x + width / 2}
          y={y - 5}
          textAnchor="middle"
          fill={meta.color || "var(--foreground)"}
          fontSize={10}
          fontWeight="bold"
        >
          {meta.label}
        </text>
      )}
    </g>
  );
};

export const DataChart = ({
  data,
  loading,
  dateFrom,
  dateTo,
  className,
  chartConfig,
  title,
  valueFormatter,
  xAxisFormatter,
  tooltipValueFormatter,
  description,
  xAxisDataKey = "period",
  referenceLine,
  showYAxis,
  flush,
  tooltipContentConfig,
  imageExportRef: _imageExportRef,
}) => {
  const { selectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    { queryOnRender: false }
  );

  const numberFormat = useNumberFormat(selectedIntegration?.currency);

  const ref = useRef(null);
  const imageExportRef = _imageExportRef || ref;
  const prevVisibleRef = useRef({});
  const prevDataRef = useRef({});

  const isMobile = useIsMobile();
  const [height, setHeight] = useState(null);

  const dataKeys = useMemo(() => Object.keys(chartConfig || {}), [chartConfig]);

  const [visibleSeries, setVisibleSeries] = useState(
    dataKeys.reduce((acc, key) => {
      acc[key] = chartConfig?.[key]?.defaultToggled ?? true;
      return acc;
    }, {})
  );

  useEffect(() => {
    const needsUpdate = dataKeys.some(
      (key) => visibleSeries[key] === undefined
    );

    if (needsUpdate) {
      setVisibleSeries((prev) =>
        dataKeys.reduce((acc, key) => {
          acc[key] = prev[key] !== undefined ? prev[key] : true;
          return acc;
        }, {})
      );
    }
  }, [dataKeys, visibleSeries]);

  const toggleSeries = (key) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const visibleDataKeys = useMemo(
    () => dataKeys.filter((key) => visibleSeries[key]),
    [dataKeys, visibleSeries]
  );

  const toggleableDataKeys = useMemo(
    () =>
      dataKeys.filter((key) => {
        if (chartConfig && chartConfig[key]?.toggleable !== undefined) {
          return chartConfig[key].toggleable;
        }
        return true;
      }),
    [dataKeys, chartConfig]
  );

  const getToggleLabel = useMemo(
    () => (key) =>
      chartConfig[key]?.shortLabel || chartConfig[key]?.label || key,
    [chartConfig]
  );

  const animationState = dataKeys.reduce((acc, key) => {
    const prevVisible = prevVisibleRef.current[key] ?? false;
    const isVisible = visibleSeries[key];

    const prevData = prevDataRef.current[key];
    const newData = data?.map((d) => d[key]);
    const dataChanged =
      prevData && newData
        ? newData.some((val, i) => val !== prevData[i])
        : true;

    // Animate if just became visible or data changed
    const shouldAnimate = (!prevVisible && isVisible) || dataChanged;

    acc[key] = { shouldAnimate };

    // Always update both refs
    prevVisibleRef.current[key] = isVisible;
    if (isVisible) {
      prevDataRef.current[key] = newData;
    }

    return acc;
  }, {});

  return (
    <Card
      className={cn("@container/card relative", className)}
      ref={imageExportRef}
    >
      <CardHeader>
        <CardTitle>
          {title}

          {selectedIntegration && (
            <span data-export-show className="hidden">
              {` (${selectedIntegration.description})`}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {loading || !data
            ? "Loading..."
            : dateFrom && dateTo
              ? `${dateFormat.default(dateFrom)} - ${dateFormat.default(dateTo)}`
              : description}
        </CardDescription>

        {/* Series Toggle */}
        <div className="mt-2 flex items-center justify-between">
          {toggleableDataKeys.length > 0 && (
            <>
              <div className="hidden gap-2 sm:flex">
                {toggleableDataKeys.map((key) => (
                  <Tooltip key={key} delayDuration={1000}>
                    <TooltipTrigger asChild>
                      <button
                        value={key}
                        aria-label={`Toggle ${chartConfig[key]?.label || key}`}
                        onClick={() => toggleSeries(key)}
                        className={cn(
                          "cursor-pointer rounded border px-2 py-0.5 text-sm font-medium transition-colors",
                          visibleSeries[key]
                            ? "dark:text-foreground text-background border-[var(--chart-color)] bg-[var(--chart-color)] dark:border-transparent dark:bg-[var(--chart-color)]/60"
                            : "text-muted-foreground border-[var(--chart-color)]/60"
                        )}
                        style={{
                          "--chart-color": chartConfig[key]?.color,
                        }}
                      >
                        {getToggleLabel(key)}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {chartConfig[key]?.label || key}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  className="absolute top-2 right-2 sm:hidden"
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="flex items-center justify-center"
                    aria-label="Toggle data series"
                  >
                    <SlidersHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Toggle Data Series</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {toggleableDataKeys.map((key) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={visibleSeries[key]}
                      onCheckedChange={() => toggleSeries(key)}
                      className="flex items-center gap-2"
                    >
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: chartConfig[key]?.color }}
                      />
                      {getToggleLabel(key)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>

        <DataExportButton
          disabled={loading}
          imageExportRef={imageExportRef}
          title={title}
          integrationName={selectedIntegration?.description}
          dateFrom={dateFrom}
          dateTo={dateTo}
          className="absolute top-2 right-12 sm:right-2"
          asIcon
        />
      </CardHeader>

      <CardContent
        className={cn(
          "px-2 sm:px-6",
          flush && (!loading || data?.length) && "-mx-7"
        )}
      >
        {loading && !data?.length ? (
          <Skeleton className="h-[14.75rem] w-full" />
        ) : (
          <ChartContainer
            config={chartConfig}
            className="h-[16rem] w-full"
            onResize={(width, height) => setHeight(height)}
          >
            <ComposedChart data={data}>
              <CartesianGrid vertical={false} />

              {[
                ...new Set(
                  visibleDataKeys.map((key) => chartConfig[key]?.yAxis || {})
                ),
              ].map(
                (
                  {
                    id = "left",
                    orientation = "left",
                    width,
                    domain,
                    tickFormatter,
                  },
                  index
                ) => {
                  return (
                    <YAxis
                      hide={!showYAxis}
                      key={id + index}
                      yAxisId={id}
                      orientation={orientation}
                      axisLine={false}
                      tickLine={false}
                      width={
                        width ||
                        Math.max(
                          60,
                          ...(data || []).map((row) =>
                            Math.max(
                              ...visibleDataKeys.map(
                                (key) =>
                                  String(
                                    (
                                      tickFormatter ||
                                      valueFormatter ||
                                      numberFormat.currency
                                    )(row[key] || 0)
                                  ).length *
                                    7 +
                                  12
                              )
                            )
                          )
                        )
                      }
                      domain={domain || undefined}
                      allowDataOverflow
                      tick={({ x, y, payload, textAnchor }) => (
                        <text
                          x={x}
                          y={y}
                          textAnchor={textAnchor}
                          className={cn(
                            "fill-muted-foreground text-xs",
                            loading && "blur-[2px]"
                          )}
                        >
                          {(
                            tickFormatter ||
                            valueFormatter ||
                            numberFormat.currency
                          )(payload.value)}
                        </text>
                      )}
                      allowDecimals
                    />
                  );
                }
              )}

              <XAxis
                dataKey={xAxisDataKey}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={5}
                tickMargin={10}
                tick={({ x, y, payload, textAnchor }) => (
                  <text
                    x={x}
                    y={y}
                    dy={16}
                    textAnchor={textAnchor}
                    className={cn(
                      "fill-muted-foreground text-xs",
                      loading && "blur-[2px]"
                    )}
                  >
                    {xAxisFormatter
                      ? xAxisFormatter(payload.value)
                      : payload.value}
                  </text>
                )}
              />

              {!loading && (
                <ChartTooltip
                  cursor={false}
                  position={{ y: isMobile && height - 25 }}
                  wrapperStyle={{
                    zIndex: 50,
                  }}
                  content={
                    <ChartTooltipContent
                      valueFormatter={(value, name, item) => {
                        const formatter =
                          chartConfig?.[item?.dataKey]?.valueFormatter ||
                          tooltipValueFormatter ||
                          valueFormatter ||
                          ((value) => numberFormat.currency(value));

                        return formatter(value, name, item);
                      }}
                      indicator="dot"
                      isItemHidden={(item) => !visibleSeries[item.dataKey]}
                      {...tooltipContentConfig}
                    />
                  }
                />
              )}

              {referenceLine && (
                <ReferenceLine
                  x={referenceLine}
                  yAxisId={chartConfig.threshold?.yAxisId || "left"}
                  stroke={chartConfig.threshold?.color || "var(--chart-2)"}
                  label={{
                    position: "top",
                    value: chartConfig.threshold?.label ?? "Threshold",
                    fill: chartConfig.threshold?.color ?? "var(--chart-2)",
                    fontSize: 10,
                  }}
                />
              )}

              {visibleDataKeys.map((key) => {
                const config = chartConfig[key];
                const type = config?.type || "area";
                const color = config?.color;
                const axisId = config?.yAxis?.id || "left";

                const { shouldAnimate } = animationState[key];

                if (type === "bar") {
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      yAxisId={axisId}
                      fill={color}
                      isAnimationActive={shouldAnimate}
                      animationDuration={600}
                      shape={<CustomBarShape />}
                    />
                  );
                }

                if (type === "line") {
                  return (
                    <Line
                      key={key}
                      dataKey={key}
                      yAxisId={axisId}
                      stroke={color}
                      strokeWidth={2}
                      dot={false}
                      type="monotone"
                      isAnimationActive={shouldAnimate}
                    />
                  );
                }

                return (
                  <Area
                    key={key}
                    dataKey={key}
                    yAxisId={axisId}
                    type="monotone"
                    fill={color}
                    strokeWidth={3}
                    stroke={color}
                    fillOpacity={0.2}
                    isAnimationActive={shouldAnimate}
                    animationDuration={600}
                  />
                );
              })}
            </ComposedChart>
          </ChartContainer>
        )}

        {/* Watermark */}
        <div
          data-export-show
          className="text-muted-foreground absolute right-2 bottom-2 flex hidden items-center gap-1 text-xs"
        >
          <Logo inline className="h-4" />
        </div>
      </CardContent>
    </Card>
  );
};
